import { unflattenTree, type StoredDestinationNode } from './tree'
import type { VisitorFilter } from '../config'
import {
  DEFAULT_DESTINATION_GROUP_ORDER,
  normalizeDestinationGroupOrder,
  type DestinationGroupKey,
} from './schema'

/**
 * Tab model for the destination popup (#1072 D1).
 *
 * Pure on purpose: the toolkit's tab layout is data, not code
 * (Settings::DEFAULTS['tabs'] declares label / source / level / nesting), so
 * the shape it produces is worth testing without a DOM. The component over
 * this file only renders what it returns.
 *
 * The toolkit's three fresh-install tabs are By Island (islands, with sides
 * nested underneath), By City (cities grouped under their island) and By
 * Resort (resorts grouped under their island, searchable). Empty tabs are
 * dropped rather than rendered blank, matching the PHP, which only emits tabs
 * whose source produced rows.
 *
 * DISPLAY ORDER (#1493 sub-item 1, toolkit destination-filter's per-feed
 * move-up/move-down groups control): `buildDestinationTabs`/
 * `computeDestinationTabs` take an optional `groupOrder` and emit surviving
 * tabs in that sequence, defaulting to the fixed islands -> cities -> resorts
 * -> city-terms order this file always used. Normalization (unknown keys
 * dropped, missing keys appended in the default sequence) lives in
 * `./schema`'s `normalizeDestinationGroupOrder`, shared with the zod leaf
 * that persists a feed's stored order.
 */

// Re-exported from schema.ts (the shared destination-tree storage contract,
// #1493) rather than declared here, so the zod leaf that persists a feed's
// groupOrder and this module's own default/normalize logic can never drift
// out of sync with the tab keys this file actually builds.
export type DestinationTabKey = DestinationGroupKey

/** Which visitor filter a node writes when picked. */
export type DestinationFilterKey = Extract<keyof VisitorFilter, 'tags' | 'locations' | 'cities'>

export type DestinationEntry = {
  id: number
  label: string
  count: number
  filterKey: DestinationFilterKey
  /** Island sides, nested under their island. */
  children: DestinationEntry[]
}

/** A heading inside a tab. `label: null` means "no heading, just the list". */
export type DestinationGroup = { key: string; label: string | null; entries: DestinationEntry[] }

export type DestinationTab = { key: DestinationTabKey; label: string; groups: DestinationGroup[] }

export const DEFAULT_DESTINATION_TAB_LABELS: Record<DestinationTabKey, string> = {
  islands: 'By Island',
  cities: 'By City',
  resorts: 'By Resort',
  'city-terms': 'By Town',
}

/** Nodes carrying a prefix live on property_tag; cities come from their own taxonomies. */
const FILTER_KEY: Record<DestinationTabKey, DestinationFilterKey> = {
  islands: 'tags',
  cities: 'locations',
  resorts: 'tags',
  'city-terms': 'cities',
}

/**
 * Identity cache over the stored rows. The tabs are read by TWO consumers that
 * both mount inside ProFilterBar — the Where field itself and the bar's
 * destination-aware summary — and each `useMemo` is its own instance, so
 * without this the whole tree is unflattened twice per options change. Keyed
 * weakly on the parsed rows array, whose identity is stable per
 * `parseWidgetConfig` and which nothing mutates in place.
 *
 * The inner Map adds a second key for `groupOrder` (#1493): a fresh `rows`
 * array is produced on every settings parse regardless of which leaf
 * changed, so in production this only ever holds one entry per rows
 * identity — but keying on the order too keeps two calls that deliberately
 * reuse the same rows reference with two different orders (tests; a future
 * caller comparing orders) from colliding on one cached result.
 */
const tabsByRows = new WeakMap<object, Map<string, DestinationTab[]>>()

export function buildDestinationTabs(
  rows: StoredDestinationNode[] | undefined,
  groupOrder: readonly DestinationTabKey[] = DEFAULT_DESTINATION_GROUP_ORDER,
): DestinationTab[] {
  if (!rows) return computeDestinationTabs(rows, groupOrder)
  const orderKey = normalizeDestinationGroupOrder(groupOrder).join(',')
  let byOrder = tabsByRows.get(rows)
  if (!byOrder) {
    byOrder = new Map()
    tabsByRows.set(rows, byOrder)
  }
  const cached = byOrder.get(orderKey)
  if (cached) return cached
  const built = computeDestinationTabs(rows, groupOrder)
  byOrder.set(orderKey, built)
  return built
}

function computeDestinationTabs(
  rows: StoredDestinationNode[] | undefined,
  groupOrder: readonly DestinationTabKey[] = DEFAULT_DESTINATION_GROUP_ORDER,
): DestinationTab[] {
  const tree = unflattenTree(rows)
  const islandLabel = new Map(tree.islands.map((i) => [i.id, i.label]))

  const entry = (
    node: { id: number; label: string; count: number },
    key: DestinationTabKey,
    children: DestinationEntry[] = [],
  ): DestinationEntry => ({
    id: node.id,
    label: node.label,
    count: node.count,
    filterKey: FILTER_KEY[key],
    children,
  })

  /**
   * Group by owning island, islands in their derived order, with unowned
   * nodes last under no heading. The PHP keeps unowned cities/resorts rather
   * than dropping them (only sides require an island), so they must stay
   * reachable in the UI.
   */
  const groupByIsland = (
    nodes: Array<{ id: number; label: string; count: number; island: number | null }>,
    key: DestinationTabKey,
  ): DestinationGroup[] => {
    const groups: DestinationGroup[] = []
    for (const island of tree.islands) {
      const entries = nodes.filter((n) => n.island === island.id).map((n) => entry(n, key))
      if (entries.length) groups.push({ key: `island-${island.id}`, label: island.label, entries })
    }
    const orphans = nodes.filter((n) => n.island === null || !islandLabel.has(n.island)).map((n) => entry(n, key))
    if (orphans.length) groups.push({ key: 'ungrouped', label: null, entries: orphans })
    return groups
  }

  // Built by key first, not pushed in a fixed sequence (#1493): a tab still
  // drops entirely when its source produced nothing, exactly as before —
  // only the SEQUENCE the surviving tabs are emitted in is now driven by
  // `groupOrder` rather than hardcoded here.
  const byKey: Partial<Record<DestinationTabKey, DestinationTab>> = {}

  if (tree.islands.length) {
    byKey.islands = {
      key: 'islands',
      label: DEFAULT_DESTINATION_TAB_LABELS.islands,
      groups: [{
        key: 'all',
        label: null,
        entries: tree.islands.map((i) => entry(i, 'islands', i.sides.map((s) => entry(s, 'islands')))),
      }],
    }
  }

  const cityGroups = groupByIsland(tree.cities, 'cities')
  if (cityGroups.length) byKey.cities = { key: 'cities', label: DEFAULT_DESTINATION_TAB_LABELS.cities, groups: cityGroups }

  const resortGroups = groupByIsland(tree.resorts, 'resorts')
  if (resortGroups.length) byKey.resorts = { key: 'resorts', label: DEFAULT_DESTINATION_TAB_LABELS.resorts, groups: resortGroups }

  if (tree.cityTerms.length) {
    byKey['city-terms'] = {
      key: 'city-terms',
      label: DEFAULT_DESTINATION_TAB_LABELS['city-terms'],
      groups: [{ key: 'all', label: null, entries: tree.cityTerms.map((c) => entry(c, 'city-terms')) }],
    }
  }

  const tabs: DestinationTab[] = []
  for (const key of normalizeDestinationGroupOrder(groupOrder)) {
    const tab = byKey[key]
    if (tab) tabs.push(tab)
  }
  return tabs
}

/**
 * Fold case, combining accents AND the ʻokina: destination names are routinely
 * accented ("Ávila"), and a visitor typing "avila" must still hit them. (The
 * plain field's legacy search is case-only and predates this surface; the panel
 * folds because it IS the destination surface.)
 *
 * NFD only decomposes COMBINING marks. The ʻokina (U+02BB) is a modifier
 * LETTER, so "Kauaʻi" survives NFD intact and "kauai" would miss it — in a
 * module whose canonical data is Hawaiian island names. Strip the whole
 * apostrophe family (ʻ, ', ’, ‘) so both spellings converge.
 */
export function foldDestinationQuery(s: string): string {
  return s
    .normalize('NFD')
    // \u escapes, not literal marks: a bare combining mark in source is
    // invisible in review and does not survive a re-encode intact.
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\u02bb\u02bc\u2018\u2019']/g, '')
    .toLowerCase()
}

/** Case/accent-insensitive substring match over the visible tab entries. */
export function filterDestinationTabs(tabs: DestinationTab[], query: string): DestinationTab[] {
  const q = foldDestinationQuery(query.trim())
  if (!q) return tabs
  const hit = (label: string) => foldDestinationQuery(label).includes(q)

  return tabs
    .map((tab) => ({
      ...tab,
      groups: tab.groups
        .map((group) => ({
          ...group,
          // A parent that matches keeps all its children; otherwise only
          // matching children survive, so a search never hides the row that
          // explains why a child is listed.
          entries: group.entries
            .map((e) => (hit(e.label) ? e : { ...e, children: e.children.filter((c) => hit(c.label)) }))
            .filter((e) => hit(e.label) || e.children.length > 0),
        }))
        .filter((group) => group.entries.length > 0),
    }))
    .filter((tab) => tab.groups.length > 0)
}

/**
 * The entries of one tab in RENDER order (each parent followed by its
 * children, group by group). This is the list the Where field's keyboard
 * navigation walks, so it must match what DestinationPanel paints — the panel
 * numbers its rows by walking the same function.
 *
 * `isExpanded` is the accordion gate: a COLLAPSED island's sides are not
 * painted, so they must not be walked either, or ArrowDown would land the
 * cursor on a row the visitor cannot see. Defaults to "everything expanded"
 * for the callers that want the whole universe (the mobile-sheet threshold
 * count).
 */
export function flattenTabEntries(
  tab: DestinationTab | undefined,
  isExpanded: (entry: DestinationEntry) => boolean = () => true,
): DestinationEntry[] {
  if (!tab) return []
  const out: DestinationEntry[] = []
  for (const group of tab.groups) {
    for (const entry of group.entries) {
      out.push(entry)
      if (entry.children.length > 0 && isExpanded(entry)) out.push(...entry.children)
    }
  }
  return out
}

/**
 * Cap the rows of each tab at `max`, counting a parent and its children
 * together in render order (a truncated island keeps whichever sides fit).
 *
 * Parity with the plain suggestion list: `searchbar.autocomplete.maxResults`
 * is documented as "Maximum destination suggestions shown" and ProWhereField
 * budget-caps `filteredSections` with it — but only while a query is active.
 * Browse mode is the curated universe in both looks, so the caller applies
 * this only when searching.
 *
 * Returns the input untouched when nothing is over budget, so the identity
 * memoization upstream keeps holding.
 */
export function limitTabEntries(tabs: DestinationTab[], max: number): DestinationTab[] {
  if (!Number.isFinite(max) || max <= 0) return tabs
  let clipped = false
  const limited = tabs.map((tab) => {
    let budget = max
    const groups: DestinationGroup[] = []
    for (const group of tab.groups) {
      if (budget <= 0) { clipped = true; break }
      const entries: DestinationEntry[] = []
      for (const entry of group.entries) {
        if (budget <= 0) { clipped = true; break }
        budget -= 1
        if (entry.children.length > budget) {
          clipped = true
          entries.push({ ...entry, children: entry.children.slice(0, Math.max(0, budget)) })
          budget = 0
        } else {
          entries.push(entry)
          budget -= entry.children.length
        }
      }
      if (entries.length) groups.push({ ...group, entries })
    }
    return groups.length === tab.groups.length && !clipped ? tab : { ...tab, groups }
  })
  return clipped ? limited : tabs
}

/**
 * Read an applied filter value back as the node's display name, so the field
 * shows "Kauai" rather than a generic "Selected destination". Searches every
 * tab and nesting level; empty when nothing resolves (a curated-out or stale
 * id must still read as applied and stay clearable — the caller handles that).
 *
 * Walks with the DEFAULT expand gate on purpose: an applied side must read
 * back as its name whether or not its island happens to be expanded.
 */
export function findDestinationLabel(
  tabs: DestinationTab[],
  filterKey: DestinationFilterKey,
  applied: string | undefined,
): string {
  const ids = (applied ?? '').split(',').filter(Boolean)
  if (!ids.length) return ''
  const names: string[] = []
  for (const id of ids) {
    for (const tab of tabs) {
      const hit = flattenTabEntries(tab).find((e) => e.filterKey === filterKey && String(e.id) === id)
      if (hit) { names.push(hit.label); break }
    }
  }
  return names.join(', ')
}

/** The three visitor-filter dimensions a destination pick can write. Every
 *  destination-mode consumer (field display, clear pill, bar summaries) must
 *  consider all three regardless of the field's own source. */
export const destinationFilterKeys: ReadonlyArray<DestinationFilterKey> = ['tags', 'locations', 'cities']

/**
 * Which of the three dimensions the destination popup may CLAIM right now —
 * for applied-state, for the clear pill, and for the clear-then-set of a pick.
 *
 * The popup is not the only writer of `tags`/`locations`/`cities`:
 *  - `visitorFilterDefaults` carries all three (config.ts) and widget.tsx
 *    seeds them into visitorFilters on load. That is the OPERATOR's baseline
 *    scope, not a visitor selection.
 *  - `chipFilterKeys` overlaps all three and QuickChips writes them. A chip is
 *    an independent, AND-able quick filter.
 *
 * Claiming those wholesale made the Where field paint "Selected destination"
 * with a clear pill before the visitor touched anything, and let one × wipe an
 * operator scope or a live chip. So: a key with a FOREIGN value on it is not
 * claimed. An empty key always is — a pick has to be able to write it, and
 * clearing an empty key is a no-op.
 *
 * A stale/curated-out id the popup itself wrote is still claimed (it is not a
 * foreign value), so it keeps reading as applied and stays clearable.
 */
export function destinationOwnedKeys(
  appliedByKey: (key: DestinationFilterKey) => string | undefined,
  foreignByKey: (key: DestinationFilterKey) => ReadonlySet<string>,
): DestinationFilterKey[] {
  return destinationFilterKeys.filter((key) => {
    const applied = appliedByKey(key)
    if (!applied) return true
    return !foreignByKey(key).has(applied)
  })
}

/**
 * The applied-label rule for destination mode, shared by ProWhereField's field
 * display and the bar summaries so they can never drift. A popup pick only
 * ever writes ONE dimension, but a deep link can carry several at once —
 * resolve them ALL, joined, so no active constraint is hidden.
 *
 * `keys` scopes it to the dimensions the field actually owns (see
 * destinationOwnedKeys) so an operator default or an active chip is never read
 * back as the visitor's destination.
 */
export function resolveDestinationAppliedLabel(
  tabs: DestinationTab[],
  appliedByKey: (key: DestinationFilterKey) => string | undefined,
  keys: ReadonlyArray<DestinationFilterKey> = destinationFilterKeys,
): string {
  const labels: string[] = []
  for (const key of keys) {
    const label = findDestinationLabel(tabs, key, appliedByKey(key))
    if (label) labels.push(label)
  }
  return labels.join(', ')
}
